<template>
  <div>
    <settings-section-title id="edit" class="i-mb-4"
      >Edit API Key</settings-section-title
    >
    <breadcrumbs :items="breadcrumbs"></breadcrumbs>

    <div
      v-if="hasEditPermissions"
      class="i-mt-12 edit-form d-flex flex-column i-gap-6"
    >
      <!-- name -->
      <i-text-input
        :value="keyName"
        label="Name"
        placeholder="Enter a name for the API Key"
        :has-error="keyHasError"
        :show-hint-text="true"
        :hint-text="
          !keyName || keyName === ''
            ? 'The API Key name field is required'
            : keyName.length > 40
              ? 'The API Key Name has a maximum length limit of 40 characters'
              : keyNameExists
                ? 'The API Key Name must be unique, another key with this name exists'
                : null
        "
        @input="keyName = $event"
      ></i-text-input>

      <!-- description -->
      <i-text-input
        label="Description (optional)"
        :value="keyDescription"
        :has-error="descriptionHasError"
        :show-hint-text="true"
        :hint-text="
          descriptionHasError
            ? 'The API Key Description field has a maximum length limit of 150 characters'
            : null
        "
        :mode="iTextInputModes.TEXTAREA"
        @input="keyDescription = $event"
      ></i-text-input>

      <div class="d-flex i-mt-6">
        <i-button
          :disabled="hasErrors"
          text="Save"
          size="lg"
          class="i-mr-6"
          @button-click="save()"
        ></i-button>

        <i-button
          text="Cancel"
          size="lg"
          type="neutral"
          subtype="subtle"
          @button-click="cancel()"
        ></i-button>
      </div>
    </div>

    <div v-else class="i-mt-6">
      <p>You must be an administrator to edit this API key</p>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import {
  iButton,
  iTextInput,
  iTextInputModes,
  showToast,
} from '@inscribe/signal';
import Breadcrumbs from '@/shared/components/Breadcrumbs';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';

export default {
  components: {
    Breadcrumbs,
    iButton,
    iTextInput,
    'settings-section-title': SettingsSectionTitle,
  },
  data() {
    return {
      iTextInputModes,
      breadcrumbs: [
        {
          text: 'Settings',
          to: { name: 'settings-view' },
        },
        {
          text: 'API Keys',
          to: { name: 'api-settings-list-keys' },
        },
        {
          text: 'Edit API Key',
          active: true,
        },
      ],
      apiKey: '',
      keyName: '',
      keyDescription: '',
      currentKey: {},
      existingKeyNames: [],
    };
  },

  computed: {
    ...mapGetters({
      hasEditPermissions: 'isOrgAdmin',
    }),

    ...mapState(['currentUser']),

    hasErrors() {
      return this.keyHasError || this.descriptionHasError;
    },

    keyHasError() {
      return (
        !this.keyName ||
        this.keyName === '' ||
        this.keyName.length > 40 ||
        this.keyNameExists
      );
    },

    descriptionHasError() {
      return this.keyDescription?.length > 150;
    },

    keyNameExists() {
      return (
        this.existingKeyNames && this.existingKeyNames.includes(this.keyName)
      );
    },
  },
  mounted() {
    this.apiKeyUUID = this.$route.params.api_key;
    this.currentKey = this.currentUser.organisation.api_keys.filter(
      (key) => key.key === this.apiKeyUUID,
    )[0];
    this.keyName = this.currentKey.name;
    this.keyDescription = this.currentKey.description;

    this.existingKeyNames = this.currentUser.organisation.api_keys.reduce(
      (results, apiKeys) => {
        if (apiKeys.key !== this.currentKey.key && apiKeys.name) {
          results.push(apiKeys.name);
        }
        return results;
      },
      [],
    );
  },
  methods: {
    ...mapActions(['editApiKey']),

    ...mapMutations(['updateApiKeyDetail']),

    cancel() {
      this.$router.push({
        name: 'api-settings-list-keys',
      });
    },
    save() {
      const keyData = {
        name: this.keyName,
        description: this.keyDescription,
      };

      this.editApiKey({
        uuid: this.apiKeyUUID,
        keyDetail: keyData,
      })
        .then((response) => {
          this.updateApiKeyDetail({
            key: this.apiKeyUUID,
            ...keyData,
          });
          showToast({
            text: `${this.keyName} has been updated`,
          });
          this.$router.push({
            name: 'api-settings-list-keys',
          });
        })
        .catch(() => {
          showToast({
            text: 'Error updating API Key',
            isError: true,
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-form {
  max-width: 500px;
}
</style>
